import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchSalesAnalytics } from '../../services/analytics/analytics-service';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const STATUS_COLORS = {
    'DONE': '#22c55e',      // GREEN
    'CLOSED': '#ef4444',    // RED
    'IN_PROGRESS': '#facc15', // YELLOW
    'OPEN': '#0ea5e9'       // BLUE
};

const getStatusColor = (status) => {
    return STATUS_COLORS[status] || '#9ca3af'; // Default gray if status not found
};

const DashboardAnalytics = () => {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        fetchSalesAnalytics().then(setAnalytics).catch(console.error);
    }, []);

    if (!analytics) {
        return <div className="text-center text-gray-600 mt-10">{t('common.loading')}...</div>;
    }

    return (
        <div className="p-6 space-y-10 max-w-screen-xl mx-auto">
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">{t('order.title')} ({t('order.status.done')})</p>
                    <p className="text-2xl font-bold">{analytics.totalOrdersDone || 0}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">{t('order.title')} {t('order.status.inProgress')}</p>
                    <p className="text-2xl font-bold">{analytics.totalOrdersInProgress || 0}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">{t('order.status.closed')} {t('order.title')}</p>
                    <p className="text-2xl font-bold">{analytics.totalOrdersClosed || 0}</p>
                </div>
            </div>

            {/* Monthly Order Count */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('dashboard.recentOrders')}</h2>
                <div className="w-full h-[300px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={analytics.monthlyOrderCount || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="orderCount" stroke="#0ea5e9" name={t('order.title')} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Order Status Distribution */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('dashboard.orderStatusDistribution')}</h2>
                <div className="w-full h-[350px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={analytics.orderStatusDistribution || []}
                                dataKey="count"
                                nameKey="status"
                                cx="50%"
                                cy="50%"
                                outerRadius={120}
                                label
                            >
                                {(analytics.orderStatusDistribution || []).map((entry, index) => (
                                    <Cell key={`cell-status-${index}`} fill={getStatusColor(entry.status)} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Top Customers by Orders */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('dashboard.topCustomers')}</h2>
                <div className="w-full h-[550px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.topCustomersByOrders || []} margin={{ top: 20, right: 30, bottom: 50, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="customerName" angle={-45} textAnchor="end" height={150} interval={0} tick={{ fontSize: 12 }} />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="orderCount" fill="#22c55e" name={t('order.title')} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Most Sold Products */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('dashboard.product.title')}</h2>
                <div className="w-full h-[550px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.mostSoldProducts || []} layout="horizontal"  margin={{ top: 20, right: 30, bottom: 120, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={180} interval={0} tick={{ fontSize: 13 }} />
                            <YAxis />
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div className="bg-white p-3 border rounded shadow-lg">
                                                <p className="font-semibold">{data.name}</p>
                                                <p className="text-sm text-gray-600">{t('product.code')}: {data.code}</p>
                                                <p className="text-sm text-gray-600">{t('product.company')}: {data.company}</p>
                                                <p className="text-sm font-bold text-green-600">{t('calculation.quantity')}: {data.quantitySold}</p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Legend />
                            <Bar dataKey="quantitySold" fill="#3b82f6" name={t('calculation.quantity')} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Product Type Distribution */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('product.type')} {t('dashboard.statistics')}</h2>
                <div className="w-full h-[550px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.productTypeDistribution || []} layout="horizontal">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="productType" angle={-45} textAnchor="end" height={180} interval={0} tick={{ fontSize: 13 }} />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="quantitySold" fill="#a855f7" name={t('calculation.quantity')} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>
        </div>
    );
};

export default DashboardAnalytics;
